<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import { ElTooltip } from "element-plus";
import {
  ref,
  watchEffect,
  computed,
  onUnmounted,
  onBeforeUpdate,
  onBeforeMount,
  PropType,
  WatchStopHandle,
  ComponentPublicInstance,
} from "vue";

import useScreenSizes from "@/hooks/screen-sizes";

/**
 * This component previews the distribution of an array of elements
 */
const props = defineProps({
  title: {
    type: String,
    default: "",
  },

  baseColor: {
    type: String,
    default: "",
  },

  card: Boolean,

  bars: {
    type: Array as PropType<{ percentage: number; color: string; label: string }[]>,
    required: true,
  },

  overlapMessage: {
    type: String,
    default: "The sum of bars is too high",
  },

  barHeight: {
    type: String,
    default: "10px",
  },

  fontSize: {
    type: String,
    default: "14px",
    validator: (value: string) => ["12px", "14px", "18px"].includes(value),
  },
});

const distributionBarRef = ref<HTMLElement>();
const barsRef = ref<Array<HTMLElement | Element | ComponentPublicInstance>>([]);

const { isMobileOrTabletScreen } = useScreenSizes();

/**
 * Getting the width of the bar with any update
 */
const { width: totalBarWidth } = useElementSize(distributionBarRef);

/**
 * Get the total percentage of the bars
 */
const total = computed(() => props.bars.reduce((acc, current) => acc + current.percentage, 0));

/**
 * Update the remaining percentage
 */
const remainingPercentage = computed(() => 100 - total.value);

const inError = computed(() => remainingPercentage.value < 0);

/**
 * Adding the last bar with the remaining percentage at the end of the distribution bar
 */
const innerBars = computed(() => {
  let results = props.bars;
  if (remainingPercentage.value > 0) {
    const remainingBar = {
      label: "Default user settings",
      percentage: remainingPercentage.value,
      color: "var(--vd-color-grey-light-1)",
    };
    results = [...props.bars, remainingBar];
  }
  return results.filter(({ percentage }) => !!percentage);
});

let stopWatcher: WatchStopHandle;

onBeforeMount(() => {
  stopWatcher = watchEffect(
    () => {
      if (distributionBarRef.value) {
        distributionBarRef.value.style.height = `calc(${props.barHeight} + ${props.fontSize} + 10px)`;
      }

      if (barsRef.value.length >= 1) {
        innerBars.value.reduce((acc, { percentage }, index) => {
          const element = barsRef.value[index] as HTMLElement;
          const { color } = innerBars.value[index];

          const currentPercentage = acc + percentage;

          const translate = `translateX(${(totalBarWidth.value * acc) / 100}px)`;
          const scale = "scaleX(var(--fx))";

          /**
           * setting the radius x for the border radius of the bar
           */
          element.style.setProperty("--fx", (percentage / 100).toFixed(2));

          const barDOM = element.querySelector<HTMLElement>(".element");
          const labelDOM = element.querySelector<HTMLElement>(".label");

          if (!barDOM || !labelDOM) {
            throw new Error("Unexpected error: barDOM or labelDOM is not defined");
          }

          /**
           * Setting the position, width and color of the bar (left position is in pixels, and size in percentage)
           */
          barDOM.style.transform = `${translate} ${scale}`;
          barDOM.style.backgroundColor = color;
          barDOM.style.borderRadius = "0";

          /**
           * Handle the case where the first elements have a percentage to 0 (hence no border-radius)
           */
          if (innerBars.value.length === 1) {
            barDOM.style.borderRadius = "calc(7.5px / var(--fx)) / calc(7.5px / var(--fx))";
          } else if (currentPercentage && acc === 0) {
            barDOM.style.borderRadius = "calc(7.5px / var(--fx)) 0 0 calc(7.5px / var(--fx)) / 7.5px";
          } else if (index === innerBars.value.length - 1) {
            barDOM.style.borderRadius = "0 calc(7.5px / var(--fx)) calc(7.5px / var(--fx)) 0 / 7.5px";
          }

          /**
           * Setting the width, position and color of the labels
           */
          // if (!isMobileOrTabletScreen) {
          labelDOM.style.transform = `${translate}`;
          labelDOM.style.color = color;
          labelDOM.style.width = `${percentage}%`;
          labelDOM.style.fontSize = props.fontSize;
          labelDOM.style.marginTop = `calc(${props.barHeight} + 8px)`;
          // }

          /**
           * If a base color is specified, generate a color for each bar with hue-rotate
           */
          if (props.baseColor) {
            barDOM.style.filter = `hue-rotate(${index * 45}deg)`;
            labelDOM.style.filter = `hue-rotate(${index * 45}deg`;
          }

          element.style.visibility = "visible";
          return currentPercentage;
        }, 0);
      }
    },
    {
      flush: "post",
    }
  );
});

/**
 * make sure to reset the refs before each update
 */
onBeforeUpdate(() => {
  barsRef.value = [];
});

onUnmounted(() => {
  stopWatcher();
});
</script>

<template>
  <div class="vd-distribution-bar-component" :class="{ 'is-card': card }">
    <h4 v-if="title">{{ title }}</h4>
    <div v-if="inError" class="alert">
      <i class="el-icon-warning" />
      {{ overlapMessage }}
    </div>
    <div v-if="!inError" ref="distributionBarRef" class="inner-distribution-bar-element">
      <div
        v-for="(bar, index) in innerBars"
        :key="index"
        :ref="
          (el) => {
            if (el) barsRef[index] = el;
          }
        "
        class="bar"
      >
        <span class="element" :style="{ height: barHeight }"></span>
        <ElTooltip placement="bottom-start" :content="`${bar.label} (${bar.percentage}%)`">
          <div class="label">
            <span class="label-content">{{ bar.label }}&nbsp;</span>
            <span v-if="!isMobileOrTabletScreen" class="label-percentage">({{ bar.percentage }}%)</span>
            <span v-else class="label-percentage">{{ bar.percentage }}%</span>
          </div>
        </ElTooltip>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.vd-distribution-bar-component {
  &.is-card {
    padding: size("M") size("L");
    @extend %card;
  }

  h4 {
    margin-bottom: size("M");
    color: var(--vd-color-blue);
    font-weight: 600;
  }

  .alert {
    display: inline-block;
    color: var(--vd-color-red);
    font-weight: 700;
  }

  .inner-distribution-bar-element {
    display: block;
    width: 100%;
    background-color: transparent;
    border-radius: 7.5px;
    position: relative;
    display: flex;
    clip-path: border-box;

    .bar {
      --fx: 1;
      position: absolute;
      width: 100%;
      height: 100%;
      visibility: hidden;
      animation: appear 1s ease;

      @include medium-screen {
        cursor: pointer;
      }

      .element {
        position: absolute;
        width: 100%;
        transform-origin: left center;
        transform: translateX(0) scaleX(0);
        transition: transform 0.5s ease;
      }

      .label {
        display: inline-block;
        // margin-top: size("XS");
        text-align: left;
        box-sizing: border-box;
        padding-left: size("XS");
        @include ellipsis;

        @include medium-screen {
          .label-content {
            display: none;
          }
        }
      }
    }
  }
}
</style>
